import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { useTranslation } from 'react-i18next';
import { AXIS_STYLE, GRID_STYLE, TOOLTIP_STYLE } from './chartTheme';

interface Metric {
  key: string;
  label: string;
  color: string;
}

interface ComparisonBarChartProps {
  data: Record<string, unknown>[];
  metrics: Metric[];
  nameKey?: string;
  layout?: 'horizontal' | 'vertical';
  stacked?: boolean;
  formatValue?: (v: number) => string;
}

export function ComparisonBarChart({
  data,
  metrics,
  nameKey = 'name',
  layout = 'vertical',
  stacked = false,
  formatValue,
}: ComparisonBarChartProps) {
  const { t } = useTranslation('common');
  if (data.length === 0) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#94a3b8', fontSize: 13 }}>
        {t('noData', 'Aucune donnée')}
      </div>
    );
  }

  const stackId = stacked ? 'stack' : undefined;

  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart
        data={data}
        layout={layout}
        margin={{ top: 4, right: 20, bottom: 4, left: layout === 'horizontal' ? 20 : 10 }}
      >
        <CartesianGrid {...GRID_STYLE} horizontal={layout === 'vertical'} vertical={layout === 'horizontal'} />
        {layout === 'vertical' ? (
          <>
            <XAxis type="number" tick={AXIS_STYLE} tickFormatter={formatValue} />
            <YAxis dataKey={nameKey} type="category" tick={AXIS_STYLE} width={110} />
          </>
        ) : (
          <>
            <XAxis dataKey={nameKey} type="category" tick={AXIS_STYLE} />
            <YAxis type="number" tick={AXIS_STYLE} tickFormatter={formatValue} />
          </>
        )}
        <Tooltip
          contentStyle={TOOLTIP_STYLE}
          formatter={(val: number, name: string) => [formatValue ? formatValue(val) : val.toLocaleString('fr-CH'), name]}
          cursor={{ fill: 'rgba(255,255,255,0.03)' }}
        />
        {metrics.length > 1 && (
          <Legend
            wrapperStyle={{ fontSize: 11, color: '#94a3b8' }}
          />
        )}
        {metrics.map((m) => (
          <Bar
            key={m.key}
            dataKey={m.key}
            name={m.label}
            fill={m.color}
            stackId={stackId}
            radius={[2, 2, 0, 0]}
            maxBarSize={40}
          />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
}
